/**
 * trackCheckoutIntent — tracks Stripe payment-link clicks and stores a local
 * fallback so guests can be shown an abandoned-checkout popup if they return.
 */

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const LOCAL_KEY = "tdf_last_checkout_intent";
const LOCAL_SUPPRESS_KEY = "tdf_checkout_popup_suppressed_until";

function getStoredToken() {
  try {
    return (
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("tdf_user_token")) ||
      null
    );
  } catch (e) {
    return null;
  }
}

function storeLocalCheckoutIntent(payload) {
  try {
    if (typeof localStorage === "undefined") return;

    localStorage.setItem(
      LOCAL_KEY,
      JSON.stringify({
        product_key: payload.product_key,
        product_title: payload.product_title,
        product_url: payload.product_url,
        product_price_pence: payload.product_price_pence,
        source_path: payload.source_path,
        email: payload.email || null,
        created_at: new Date().toISOString(),
        dismissed: false,
      })
    );
  } catch (e) {
    // Silent by design.
  }
}

export function clearLocalCheckoutIntent() {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(LOCAL_KEY);
  } catch (e) {
    // Silent by design.
  }
}

export function suppressCheckoutPopup(hours = 24) {
  try {
    if (typeof localStorage === "undefined") return;
    const until = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem(LOCAL_SUPPRESS_KEY, String(until));
  } catch (e) {
    // Silent by design.
  }
}

export function getLocalCheckoutIntent() {
  try {
    if (typeof localStorage === "undefined") return null;

    const suppressUntil = Number(localStorage.getItem(LOCAL_SUPPRESS_KEY) || 0);

    if (suppressUntil && Date.now() < suppressUntil) {
      return null;
    }

    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    if (!data || !data.product_url || !data.created_at) {
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
}

export function trackCheckoutIntent({
  productKey,
  productTitle,
  productUrl,
  productPricePence,
  email,
  sourcePath,
}) {
  if (!productKey || !productUrl) return;

  const payload = {
    product_key: String(productKey),
    product_title: String(productTitle || productKey),
    product_url: String(productUrl),
    product_price_pence: productPricePence ?? null,
    email: email || null,
    source_path:
      sourcePath ||
      (typeof window !== "undefined" ? window.location.pathname : null),
  };

  // Store local copy for guest/on-screen popup recovery.
  storeLocalCheckoutIntent(payload);

  const token = getStoredToken();

  /**
   * Important:
   * navigator.sendBeacon cannot send custom Authorization headers.
   * Therefore, if we have a token or email, use fetch first so the backend can
   * attach the checkout intent to a known buyer.
   */
  if (token || email) {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      fetch(`${API}/checkout/intent`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
      }).catch(() => {});

      return;
    } catch (e) {
      // Fall through to sendBeacon.
    }
  }

  // Guest/no-email path.
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });

      const ok = navigator.sendBeacon(`${API}/checkout/intent`, blob);

      if (ok) return;
    }
  } catch (e) {
    // Fall through to fetch.
  }

  try {
    fetch(`${API}/checkout/intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch (e) {
    // Truly silent.
  }
}

export default trackCheckoutIntent;